import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, Clock, CheckCircle2, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { contactInfo, serviceOptions } from '../data/mock';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

// Clé de site publique Google reCAPTCHA v3 (la clé secrète reste côté serveur).
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfqYLYtAAAAAHHdnkpyA8yjXcOdqWeQqXwQkSM1';

const CONTACT_ENDPOINT = '/backend/contact.php';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    services: [],
    message: ''
  });
  const [honeypot, setHoneypot] = useState('');
  // reCAPTCHA v3 (invisible) : script injecté uniquement dans un vrai
  // navigateur. navigator.webdriver = true pendant le crawl react-snap, donc
  // le HTML pré-rendu ne change pas (aucun mismatch d'hydratation possible).
  useEffect(() => {
    if (navigator.webdriver) return;
    if (document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]')) return;
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    document.head.appendChild(s);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceValue) => {
    setFormData(prev => {
      const isSelected = prev.services.includes(serviceValue);
      return {
        ...prev,
        services: isSelected
          ? prev.services.filter(s => s !== serviceValue)
          : [...prev.services, serviceValue]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.services.length === 0) {
      toast.error('Veuillez sélectionner au moins un service');
      return;
    }

    // Anti-spam : honeypot (rempli uniquement par des robots)
    if (honeypot) {
      return;
    }

    // Anti-spam : Google reCAPTCHA v3 (invisible)
    let recaptchaToken = null;
    try {
      await new Promise((resolve, reject) => {
        if (!window.grecaptcha) return reject(new Error('grecaptcha absent'));
        window.grecaptcha.ready(resolve);
      });
      recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
    } catch (err) {
      toast.error("Le contrôle anti-robot n'a pas pu être chargé. Rechargez la page.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(CONTACT_ENDPOINT, {
        ...formData,
        website: honeypot,
        'g-recaptcha-response': recaptchaToken,
      });
      toast.success('Votre demande a été envoyée avec succès ! Nous vous recontacterons sous 24h.');
      setFormData({ name: '', email: '', phone: '', services: [], message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet defer={false}>
        <title>Contact & Devis gratuit | LYNKO</title>
        <meta name="description" content="Contactez LYNKO pour un devis gratuit : installation PC, imprimantes, téléphonie IP, réseau, dépannage et maintenance. Tél : +33 7 49 45 13 45." />
        <link rel="canonical" href="https://lynko.info/contact" />
        <meta property="og:title" content="Contact & Devis gratuit | LYNKO" />
        <meta property="og:url" content="https://lynko.info/contact" />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Contactez-nous
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Demandez votre devis gratuit ou posez-nous vos questions. Nous vous répondons sous 24h.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Left - Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Formulaire de contact</h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jean.dupont@exemple.fr"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium mb-3 block">Services souhaités *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      {serviceOptions.map((option) => {
                        const isSelected = formData.services.includes(option.value);
                        return (
                          <button
                            type="button"
                            key={option.value}
                            onClick={() => handleServiceToggle(option.value)}
                            className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Décrivez votre projet ou votre besoin..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="mt-1.5 resize-none"
                    />
                  </div>

                  {/* Honeypot anti-spam (invisible pour les humains) */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />

                  {/* Anti-spam : Google reCAPTCHA v3 (invisible, navigateur uniquement) */}
                  <p className="text-xs text-gray-400 leading-relaxed" data-testid="recaptcha-notice">
                    {'Ce site est protégé par reCAPTCHA et Google. '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-500">Règles de confidentialité</a>
                    {' et '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-500">Conditions d'utilisation</a>
                    {' de Google s\'appliquent.'}
                  </p>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 h-auto transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Envoyer ma demande
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Right - Contact Info Cards */}
            <div className="lg:col-span-2 space-y-4">
              {/* Phone Card */}
              <div id="telephone" className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Téléphone</h3>
                    <p className="text-gray-600 text-sm">Appelez-nous pour un devis rapide</p>
                    <span className="text-blue-500 font-semibold select-all">
                      {contactInfo.phone}
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Email</h3>
                    <p className="text-gray-600 text-sm">Envoyez-nous un message</p>
                    <a href={`mailto:${contactInfo.email}`} className="text-blue-500 font-semibold hover:underline">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Horaires</h3>
                    <p className="text-gray-700 text-sm">{contactInfo.hours.weekdays}</p>
                    <p className="text-gray-700 text-sm">{contactInfo.hours.saturday}</p>
                    <p className="text-gray-700 text-sm font-medium">{contactInfo.hours.emergency}</p>
                  </div>
                </div>
              </div>

              {/* Free Quote Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                <h3 className="font-bold text-lg mb-2">Devis Gratuit</h3>
                <p className="text-blue-100 text-sm mb-3">Recevez une estimation personnalisée sous 24h</p>
                <div className="flex items-center gap-2 text-white">
                  <CheckCircle2 className="w-5 h-5 text-green-300" />
                  <span className="text-sm font-medium">Sans engagement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
